import { useId, type ReactNode } from "react"
import cx from "clsx"
import KnobIcon from "../assets/icon-switch-knob.svg?react"
import { describedBy } from "./describedBy"
import { FieldHint } from "./FieldHint"
import styles from "./Switch.module.css"

type SwitchProps = {
  checked: boolean
  onChange: (checked: boolean) => void
  /** Text above the switch, as on any form field, which also names it. */
  label?: ReactNode
  /** The name when there is no label beside it to carry one. */
  ariaLabel?: string
  /** How to answer it, shown under the label and read with the control. */
  hint?: string
  /** What is wrong with it, shown under the switch and read with it. */
  error?: string
  required?: boolean
  busy?: boolean
  id?: string
}

/**
 * Components/Controls/Switch: on or off, taking effect as it is switched.
 *
 * A button with `role="switch"`, so Space and Enter both switch it. The On/Off
 * text is hidden from assistive tech, which hears `aria-checked` say the same
 * thing.
 *
 * `busy` marks it in progress and not to be used, without `disabled`, which
 * would drop keyboard focus mid-interaction. A press is still reported: the
 * caller drops it, and the switch says so rather than silently eating it.
 */
export function Switch({
  checked,
  onChange,
  label,
  ariaLabel,
  hint,
  error,
  required = false,
  busy = false,
  id,
}: SwitchProps) {
  const generatedId = useId()
  const switchId = id ?? generatedId
  return (
    <span
      className={
        label == null && hint == null
          ? styles.root
          : cx(styles.field, { [styles.error]: error })
      }
    >
      {label != null && (
        <label className={styles.label} htmlFor={switchId}>
          {label}
          {required && (
            <span aria-hidden="true" className={styles.required}>
              {" "}
              *
            </span>
          )}
        </label>
      )}
      {hint && <FieldHint id={`${switchId}-hint`}>{hint}</FieldHint>}
      <button
        id={switchId}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        aria-describedby={describedBy(
          hint && `${switchId}-hint`,
          error && `${switchId}-error`,
        )}
        aria-invalid={!!error || undefined}
        aria-required={required || undefined}
        aria-busy={busy || undefined}
        aria-disabled={busy || undefined}
        className={cx(styles.track, checked ? styles.on : styles.off)}
        onClick={() => onChange(!checked)}
      >
        {!checked && <KnobIcon className={styles.knob} aria-hidden="true" />}
        <span className={styles.state} aria-hidden="true">
          {checked ? "On" : "Off"}
        </span>
        {checked && <KnobIcon className={styles.knob} aria-hidden="true" />}
      </button>
      {error && (
        <span
          id={`${switchId}-error`}
          className={styles.errorMessage}
          aria-live="polite"
        >
          {error}
        </span>
      )}
    </span>
  )
}
